import { useState } from 'react';
import { UserPlus, Mail, Eye, Edit, Trash2, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTheme } from '@/context/ThemeContext';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

const ProjectLeaders = () => {
  const { isDark } = useTheme();
  const [leaders] = useState([
    {
      id: 'PL-001',
      name: 'John Doe',
      email: 'john@example.com',
      projects: ['EcoTrack', 'SkillSwap'],
      projectsCount: 2,
      status: 'Active',
      joinedAt: '01/09/2025',
      role: 'Project Leader',
    },
    {
      id: 'PL-002',
      name: 'Jane Smith',
      email: 'jane@example.com',
      projects: ['LocalVoice'],
      projectsCount: 1,
      status: 'Active',
      joinedAt: '05/09/2025',
      role: 'Project Leader',
    },
    {
      id: 'PL-003',
      name: 'Mike Johnson',
      email: 'mike@example.com',
      projects: ['MediRemind'],
      projectsCount: 1,
      status: 'Active',
      joinedAt: '10/08/2025',
      role: 'Core Head',
    },
    {
      id: 'PL-004',
      name: 'Sarah Williams',
      email: 'sarah@example.com',
      projects: [],
      projectsCount: 0,
      status: 'Inactive',
      joinedAt: '15/07/2025',
      role: 'Project Leader',
    },
  ]);

  const handleAddLeader = () => {
    // TODO: Implement add leader
    console.log('Adding new project leader...');
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0">
        <div>
          <h2 className={`text-2xl sm:text-3xl font-bold mb-2 transition-colors ${
            isDark ? 'text-white' : 'text-gray-900'
          }`}>Project Leaders</h2>
          <p className={`text-sm sm:text-base ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Manage project leaders and core heads</p>
        </div>
        <Button 
          onClick={handleAddLeader} 
          className="w-full sm:w-auto bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white text-sm sm:text-base"
        >
          <UserPlus className="w-4 h-4 mr-2" />
          Add Leader
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
        <div className={`rounded-lg p-4 border transition-colors shadow-sm ${
          isDark ? 'bg-gradient-to-br from-[#2d1b4e] to-[#1a0b2e] border-purple-500/20' : 'bg-white border-gray-200'
        }`}>
          <p className={`text-sm transition-colors ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Total Leaders</p>
          <p className={`text-2xl font-bold transition-colors ${isDark ? 'text-white' : 'text-gray-900'}`}>{leaders.length}</p>
        </div>
        <div className={`rounded-lg p-4 border transition-colors shadow-sm ${
          isDark ? 'bg-gradient-to-br from-[#2d1b4e] to-[#1a0b2e] border-purple-500/20' : 'bg-white border-gray-200'
        }`}>
          <p className={`text-sm transition-colors ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Active</p>
          <p className="text-2xl font-bold text-green-600">
            {leaders.filter(l => l.status === 'Active').length}
          </p>
        </div>
        <div className={`rounded-lg p-4 border transition-colors shadow-sm ${
          isDark ? 'bg-gradient-to-br from-[#2d1b4e] to-[#1a0b2e] border-purple-500/20' : 'bg-white border-gray-200'
        }`}>
          <p className={`text-sm transition-colors ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Core Heads</p>
          <p className="text-2xl font-bold text-purple-600">
            {leaders.filter(l => l.role === 'Core Head').length}
          </p>
        </div>
        <div className={`rounded-lg p-4 border transition-colors shadow-sm ${
          isDark ? 'bg-gradient-to-br from-[#2d1b4e] to-[#1a0b2e] border-purple-500/20' : 'bg-white border-gray-200'
        }`}>
          <p className={`text-sm transition-colors ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Total Projects</p>
          <p className="text-2xl font-bold text-blue-600">
            {leaders.reduce((sum, l) => sum + l.projectsCount, 0)}
          </p>
        </div>
      </div>

      {/* Leaders - Desktop Table / Mobile Cards */}
      <div className={`rounded-xl shadow-md border overflow-hidden transition-colors ${
        isDark ? 'bg-gradient-to-br from-[#2d1b4e] to-[#1a0b2e] border-purple-500/20' : 'bg-white border-gray-200'
      }`}>
        {/* Desktop Table View */}
        <div className="hidden md:block overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className={isDark ? 'bg-purple-500/10 border-purple-500/20' : 'bg-gray-50'}>
                <TableHead className={`font-semibold transition-colors ${
                  isDark ? 'text-gray-300' : 'text-gray-700'
                }`}>ID</TableHead>
                <TableHead className={`font-semibold transition-colors ${
                  isDark ? 'text-gray-300' : 'text-gray-700'
                }`}>NAME</TableHead>
                <TableHead className={`font-semibold transition-colors ${
                  isDark ? 'text-gray-300' : 'text-gray-700'
                }`}>EMAIL</TableHead>
                <TableHead className={`font-semibold transition-colors ${
                  isDark ? 'text-gray-300' : 'text-gray-700'
                }`}>ROLE</TableHead>
                <TableHead className={`font-semibold transition-colors ${
                  isDark ? 'text-gray-300' : 'text-gray-700'
                }`}>PROJECTS</TableHead>
                <TableHead className={`font-semibold transition-colors ${
                  isDark ? 'text-gray-300' : 'text-gray-700'
                }`}>STATUS</TableHead>
                <TableHead className={`font-semibold transition-colors ${
                  isDark ? 'text-gray-300' : 'text-gray-700'
                }`}>JOINED AT</TableHead>
                <TableHead className={`font-semibold transition-colors ${
                  isDark ? 'text-gray-300' : 'text-gray-700'
                }`}>ACTIONS</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {leaders.map((leader) => (
                <tr
                  key={leader.id}
                  className={`border-b transition-colors group ${
                    isDark 
                      ? 'border-purple-500/20 hover:bg-purple-500/10' 
                      : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <TableCell className={`font-medium transition-colors ${
                    isDark ? 'text-gray-200 group-hover:text-white' : 'text-gray-900'
                  }`}>{leader.id}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {leader.role === 'Core Head' && (
                        <Shield className={`w-4 h-4 transition-colors ${
                          isDark ? 'text-purple-400 group-hover:text-purple-300' : 'text-purple-600'
                        }`} />
                      )}
                      <span className={`font-medium transition-colors ${
                        isDark ? 'text-gray-200 group-hover:text-white' : 'text-gray-900'
                      }`}>{leader.name}</span>
                    </div>
                  </TableCell>
                  <TableCell className={isDark ? 'text-gray-300 group-hover:text-white' : 'text-gray-700'}>{leader.email}</TableCell>
                  <TableCell>
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                        leader.role === 'Core Head'
                          ? isDark 
                            ? 'bg-purple-500/30 text-purple-200' 
                            : 'bg-purple-100 text-purple-700'
                          : isDark
                            ? 'bg-blue-500/30 text-blue-200'
                            : 'bg-blue-100 text-blue-700'
                      }`}
                    >
                      {leader.role}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div>
                      <p className={`font-medium transition-colors ${
                        isDark ? 'text-gray-200 group-hover:text-white' : 'text-gray-900'
                      }`}>{leader.projectsCount} projects</p>
                      {leader.projects.length > 0 && (
                        <p className={`text-xs transition-colors ${
                          isDark ? 'text-gray-400 group-hover:text-gray-300' : 'text-gray-500'
                        }`}>{leader.projects.join(', ')}</p>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                        leader.status === 'Active'
                          ? isDark
                            ? 'bg-green-500/30 text-green-200'
                            : 'bg-green-100 text-green-700'
                          : isDark
                            ? 'bg-gray-500/30 text-gray-300'
                            : 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      {leader.status}
                    </span>
                  </TableCell>
                  <TableCell className={isDark ? 'text-gray-300 group-hover:text-white' : 'text-gray-700'}>{leader.joinedAt}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="sm" className={`h-8 w-8 p-0 ${
                        isDark ? 'text-gray-300 hover:text-white hover:bg-purple-500/20' : ''
                      }`}>
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" className={`h-8 w-8 p-0 ${
                        isDark ? 'text-gray-300 hover:text-white hover:bg-purple-500/20' : ''
                      }`}>
                        <Mail className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" className={`h-8 w-8 p-0 ${
                        isDark ? 'text-gray-300 hover:text-white hover:bg-purple-500/20' : ''
                      }`}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" className={`h-8 w-8 p-0 ${
                        isDark 
                          ? 'text-red-400 hover:text-red-300 hover:bg-red-500/20' 
                          : 'text-red-600'
                      }`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </tr>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Mobile Card View */}
        <div className="md:hidden divide-y divide-purple-500/20">
          {leaders.map((leader) => (
            <div
              key={leader.id}
              className={`p-4 transition-colors ${
                isDark 
                  ? 'hover:bg-purple-500/10' 
                  : 'hover:bg-gray-50'
              }`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    {leader.role === 'Core Head' && (
                      <Shield className={`w-4 h-4 transition-colors ${
                        isDark ? 'text-purple-400' : 'text-purple-600'
                      }`} />
                    )}
                    <span className={`text-xs font-semibold transition-colors ${
                      isDark ? 'text-purple-300' : 'text-purple-600'
                    }`}>{leader.id}</span>
                    <span
                      className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        leader.role === 'Core Head'
                          ? isDark 
                            ? 'bg-purple-500/30 text-purple-200' 
                            : 'bg-purple-100 text-purple-700'
                          : isDark
                            ? 'bg-blue-500/30 text-blue-200'
                            : 'bg-blue-100 text-blue-700'
                      }`}
                    >
                      {leader.role}
                    </span>
                    <span
                      className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        leader.status === 'Active'
                          ? isDark
                            ? 'bg-green-500/30 text-green-200'
                            : 'bg-green-100 text-green-700'
                          : isDark
                            ? 'bg-gray-500/30 text-gray-300'
                            : 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      {leader.status}
                    </span>
                  </div>
                  <h3 className={`font-semibold mb-1 transition-colors ${
                    isDark ? 'text-white' : 'text-gray-900'
                  }`}>{leader.name}</h3>
                  <p className={`text-xs mb-2 transition-colors ${
                    isDark ? 'text-gray-400' : 'text-gray-500'
                  }`}>{leader.email}</p>
                </div>
                <div className="flex items-center gap-1 ml-2 flex-shrink-0">
                  <Button variant="ghost" size="sm" className={`h-7 w-7 p-0 ${
                    isDark ? 'text-gray-300 hover:text-white hover:bg-purple-500/20' : ''
                  }`}>
                    <Eye className="w-3.5 h-3.5" />
                  </Button>
                  <Button variant="ghost" size="sm" className={`h-7 w-7 p-0 ${
                    isDark ? 'text-gray-300 hover:text-white hover:bg-purple-500/20' : ''
                  }`}>
                    <Mail className="w-3.5 h-3.5" />
                  </Button>
                  <Button variant="ghost" size="sm" className={`h-7 w-7 p-0 ${
                    isDark ? 'text-gray-300 hover:text-white hover:bg-purple-500/20' : ''
                  }`}>
                    <Edit className="w-3.5 h-3.5" />
                  </Button>
                  <Button variant="ghost" size="sm" className={`h-7 w-7 p-0 ${
                    isDark 
                      ? 'text-red-400 hover:text-red-300 hover:bg-red-500/20' 
                      : 'text-red-600'
                  }`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className={`text-xs mb-1 transition-colors ${
                    isDark ? 'text-gray-400' : 'text-gray-500'
                  }`}>Projects</p>
                  <p className={`font-medium transition-colors ${
                    isDark ? 'text-gray-300' : 'text-gray-700'
                  }`}>{leader.projectsCount} projects</p>
                  {leader.projects.length > 0 && (
                    <p className={`text-xs mt-1 transition-colors ${
                      isDark ? 'text-gray-400' : 'text-gray-500'
                    }`}>{leader.projects.join(', ')}</p>
                  )}
                </div>
                <div>
                  <p className={`text-xs mb-1 transition-colors ${
                    isDark ? 'text-gray-400' : 'text-gray-500'
                  }`}>Joined At</p>
                  <p className={`font-medium transition-colors ${
                    isDark ? 'text-gray-300' : 'text-gray-700'
                  }`}>{leader.joinedAt}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ProjectLeaders;

